import { useId, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "@carepass/ui";
import { ApiError } from "@carepass/api-client";
import { memberApi } from "../shared/api";
import type { Transaction, TransactionGroup } from "../shared/types";
import { Alert, Button, money, navigate } from "../shared/ui";
import { cacheTransactionResult } from "./transaction-cache";
import { transactionPath } from "./transaction-groups";
import { presentedTransactionActions, type PresentedTransactionAction, type TransactionActionCode } from "./transaction-actions";
import "./TransactionActions.css";

export function TransactionActions({ transaction, group, layout = "detail", codes }: {
  readonly transaction: Transaction;
  readonly group?: TransactionGroup;
  readonly layout?: "detail" | "row";
  readonly codes?: readonly TransactionActionCode[];
}) {
  const client = useQueryClient();
  const descriptionId = useId();
  const [guide, setGuide] = useState<PresentedTransactionAction>();
  const [confirming, setConfirming] = useState(false);
  const [cancelKind, setCancelKind] = useState<"CANCEL_TRANSACTION" | "CANCEL_CARD_PAYMENT">("CANCEL_TRANSACTION");
  const [error, setError] = useState<string>();
  const [requestKey, setRequestKey] = useState(() => globalThis.crypto.randomUUID());
  const submitting = useRef(false);
  const actions = presentedTransactionActions(transaction, group).filter(action => !codes || codes.includes(action.code));
  const cancel = useMutation({
    mutationFn: () => cancelKind === "CANCEL_CARD_PAYMENT" ? memberApi.cancelCardPayment(transaction.id, requestKey) : memberApi.cancelTransaction(transaction.id, requestKey),
    onSuccess: result => { cacheTransactionResult(client, result); setConfirming(false); setError(undefined); },
    onError: async cause => {
      setConfirming(false);
      if (cause instanceof ApiError && cause.code === "PAYMENT_CANCELLATION_FAILED") setRequestKey(globalThis.crypto.randomUUID());
      setError(cause instanceof ApiError || cause instanceof Error ? cause.message : "거래 취소를 처리하지 못했습니다. 최신 거래 상태를 확인해 주세요.");
      // Another tab/admin may have changed the status between display and execution.
      try { cacheTransactionResult(client, await memberApi.transaction(transaction.id)); }
      catch { void client.invalidateQueries({ queryKey: ["member", "transaction", transaction.id] }); }
    },
    onSettled: () => { submitting.current = false; },
  });
  const activate = (action: PresentedTransactionAction) => {
    if (!action.allowed) { setGuide(action); return; }
    if (action.code === "CANCEL_TRANSACTION" || action.code === "CANCEL_CARD_PAYMENT") { setCancelKind(action.code); setError(undefined); setConfirming(true); return; }
    if (action.route) navigate(action.route);
  };
  const statusGuide = presentedTransactionActions(transaction, group).find(action => action.allowed && ["VIEW_PAYMENT_STATUS", "VIEW_TRANSFER_STATUS"].includes(action.code));
  if (actions.length === 0 && !error) return null;
  return <div className={`member-transaction-actions member-transaction-actions--${layout}`}>
    {error && <Alert persistent variant="danger" title="거래 상태를 다시 확인해 주세요">{error}</Alert>}
    <div className="member-transaction-actions__buttons">
      {actions.map(action => <Button key={action.code} type="button" variant={action.primary ? "primary" : "secondary"}
        data-action={action.code} data-primary={action.primary || undefined} data-blocked={!action.allowed || undefined}
        aria-label={action.allowed ? action.label : `${action.label} · 안내`}
        onClick={() => activate(action)}>{action.label}{!action.allowed && <span className="member-transaction-actions__guide-label">안내</span>}</Button>)}
    </div>
    <Dialog open={Boolean(guide)} title={`${guide?.label ?? "이 행동"} 안내`} onClose={() => setGuide(undefined)}
      description={guide?.blockedReasonMessage?.trim() || "현재 이 행동을 진행할 수 없습니다. 거래 상세에서 최신 상태를 확인해 주세요."}
      primaryAction={<Button onClick={() => { setGuide(undefined); navigate(statusGuide?.route ?? transactionPath(`/app/transactions/${transaction.id}`, group)); }}>{statusGuide?.label ?? "거래 상세 확인"}</Button>}
      secondaryAction={<Button variant="secondary" onClick={() => setGuide(undefined)}>닫기</Button>} />
    <Dialog className="member-transaction-cancel" open={confirming} title={cancelKind === "CANCEL_CARD_PAYMENT" ? "카드 결제를 전액 취소할까요?" : "이 거래를 취소할까요?"} description={cancelKind === "CANCEL_CARD_PAYMENT" ? "카드 결제 취소가 완료되면 거래도 취소되고 사용한 한도가 복원됩니다." : "거래를 취소하면 예약된 한도를 다시 사용할 수 있습니다."}
      closeDisabled={cancel.isPending} onClose={() => { if (!submitting.current) setConfirming(false); }}
      secondaryAction={<Button variant="secondary" size="large" disabled={cancel.isPending} onClick={() => setConfirming(false)}>돌아가기</Button>}
      primaryAction={<Button variant="danger" size="large" isLoading={cancel.isPending} aria-describedby={descriptionId} onClick={() => {
        if (!submitting.current) { submitting.current = true; cancel.mutate(); }
      }}>{cancelKind === "CANCEL_CARD_PAYMENT" ? "결제 취소 확정" : "거래 취소 확정"}</Button>}>
      {cancelKind === "CANCEL_CARD_PAYMENT" && <dl className="member-transaction-cancel__summary">
        <div className="member-transaction-cancel__transaction"><dt>거래명</dt><dd>{transaction.title}</dd></div>
        <div className="member-transaction-cancel__amount"><dt>전액 취소 금액</dt><dd>{money(transaction.approvedAmount ?? transaction.requestedAmount)}</dd></div>
      </dl>}
      <p className="member-transaction-cancel__notice" id={descriptionId}>취소한 거래는 다시 진행할 수 없습니다. 필요한 경우 새 거래를 등록해 주세요.</p>
    </Dialog>
  </div>;
}
